import { Avatar } from "../../components/ui";
import { CONTACT_TYPE_LABEL, formatPhone, STATUS_LABEL, shortTime } from "../../lib/format";
import { useConversation, useTeam } from "../../lib/queries";
import type { ConversationEvent } from "../../lib/types";
import { ContactCases } from "../cases/ContactCases";
import { ContactForm } from "../contacts/ContactForm";

/** Why the IA handed a conversation to the team, in the panel's words. */
const AI_HANDOFF: Record<string, string> = {
  ai_handoff: "La IA la pasó a un asesor",
  ai_check: "La IA la pasó a un asesor: su respuesta no pasó la revisión y no se envió",
  ai_limit: "La IA la pasó a un asesor: llegó al límite de respuestas del día",
  ai_error: "La IA la pasó a un asesor: no pudo responder",
  off_hours: "Llegó fuera de horario: se envió el mensaje fijo y quedó para el equipo",
};

function describe(e: ConversationEvent, names: Map<string, string>) {
  const data = e.data ?? {};
  if (e.actorId === "agent_ai" && typeof data.reason === "string" && AI_HANDOFF[data.reason]) {
    return AI_HANDOFF[data.reason];
  }
  const who = e.actorId === "agent_ai" ? "La IA" : e.actorId ? (names.get(e.actorId) ?? "Alguien") : "El sistema";
  if (e.type === "assigned") {
    const to = names.get(String(data.assigneeId)) ?? "otra persona";
    return data.area ? `${who} la asignó a ${to} (área ${String(data.area)})` : `${who} la asignó a ${to}`;
  }
  if (e.type === "unassigned") return `${who} la dejó sin asignar`;
  const to = STATUS_LABEL[data.to as keyof typeof STATUS_LABEL] ?? String(data.to);
  return data.reason === "agent_reply" ? `${who} respondió y la tomó` : `${who} la pasó a ${to.toLowerCase()}`;
}

/** Right column of the inbox: who the customer is, editable, plus what happened. */
export function ContactPanel({ conversationId, inSheet = false }: { conversationId: string; inSheet?: boolean }) {
  const conv = useConversation(conversationId);
  const team = useTeam();
  const className = inSheet ? "contact-panel in-sheet" : "contact-panel";
  if (!conv.data) return <aside className={className} />;

  const { contact, events } = conv.data;
  const names = new Map(team.data?.items.map((m) => [m.id, m.name]));

  return (
    <aside className={className} aria-label="Ficha del contacto">
      <div className="who">
        <Avatar name={contact.name} fallback={contact.waId} />
        <strong>{contact.name ?? "Sin nombre"}</strong>
        <span className="muted mono">{formatPhone(contact.waId)}</span>
        {contact.type && <span className="tag">{CONTACT_TYPE_LABEL[contact.type]}</span>}
      </div>

      <div className="section">
        <h3>Datos</h3>
        <ContactForm key={contact.updatedAt} contact={contact} compact />
      </div>

      <div className="section">
        <h3>Postventa y ventas</h3>
        <ContactCases contact={contact} conversationId={conversationId} />
      </div>

      <div className="section">
        <h3>Historial</h3>
        <div className="events">
          {events.length === 0 && <span>Sin movimientos todavía.</span>}
          {events.map((e) => (
            <span key={e.id}>
              {shortTime(e.createdAt)} · {describe(e, names)}
            </span>
          ))}
        </div>
      </div>
    </aside>
  );
}
